/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import ErrorName from '../../../../core/error/error-name';

import FileSystemDirectory from './file-system-directory';
import FileSystemFile from './file-system-file';
import FileSystemUnit from './file-system-unit';

/**
 * @import { FileSystemManager as IFileSystemManager } from '../../../../core/file/system'
 * @import FileSystemPath from '../../../../core/file/system/file-system-path'
 */

/**
 * @typedef {{handle: FileSystemFileHandle | FileSystemDirectoryHandle; path: FileSystemPath}[]} FileSystemDirectoryList
 */

/**
 * Triviální správce souborového systému pro webovou platformu.
 * @implements {IFileSystemManager}
 */
export default class FileSystemManager {
  /**
   * Vytvoří instanci.
   * @param {StorageManager} storage
   */
  constructor(storage) {
    // Existuje TypeMismatchError: The path supplied exists, but was not an entry of requested type.
    // Ak sa snažím získať handle na súbor, ktorý je adresár a opačne.
    this.storage = storage;
  }

  /** @type {IFileSystemManager['createDirectory']} */
  async createDirectory(path) {
    if (await this.hasDirectory(path)) {
      throw pathExistsError('Directory', path.getValue());
    }
    const parent = await this.getDirectParent(path, { create: true });
    const directory = await parent.getDirectoryHandle(path.getBase(), {
      create: true
    });
    return new FileSystemDirectory(directory, this, path);
  }

  /** @type {IFileSystemManager['getAllUnits']} */
  async getAllUnits() {
    return await [new FileSystemUnit(this, this.storage)];
  }

  /** @type {IFileSystemManager['getDirectory']} */
  async getDirectory(path) {
    const directoryHandle = await this.getDirectoryHandle(path);
    return new FileSystemDirectory(directoryHandle, this, path);
  }

  /**
   * @param {FileSystemPath} path
   * @returns {Promise<FileSystemDirectoryHandle>}
   */
  async getDirectoryHandle(path) {
    if (!(await this.hasDirectory(path))) {
      throw notFoundError('Directory');
    }

    const parent = await this.getDirectParent(path);
    if (await parent.isSameEntry(await this.storage.getDirectory())) {
      return parent;
    }
    return await parent.getDirectoryHandle(path.getBase(), { create: false });
  }

  /**
   * @param {FileSystemPath} path
   * @param {Object} [opts={}]
   * @param {Boolean} [opts.create]
   * @returns {Promise<FileSystemDirectoryHandle>}
   */
  async getDirectParent(path, { create = false } = {}) {
    const segments = path.getSegments();
    // Zajímá nás nadřazený adresář.
    const parents = segments.slice(0, -1);

    const root = await this.storage.getDirectory();
    return await parents.reduce(
      (promiseParent, child) =>
        promiseParent.then((parent) =>
          parent.getDirectoryHandle(child, { create })
        ),
      Promise.resolve(root)
    );
  }

  /** @type {IFileSystemManager['getEntry']} */
  async getEntry(path) {
    if (await this.hasFile(path)) {
      return await this.getFile(path);
    } else if (await this.hasDirectory(path)) {
      return await this.getDirectory(path);
    }
    throw notFoundError('Entry');
  }

  /** @type {IFileSystemManager['getFile']} */
  async getFile(path) {
    const fileHandle = await this.getFileHandle(path);
    return new FileSystemFile(path, fileHandle, this);
  }

  /**
   * @param {FileSystemPath} path
   * @returns {Promise<FileSystemFileHandle>}
   */
  async getFileHandle(path) {
    if (!(await this.hasFile(path))) {
      throw notFoundError('File');
    }

    const parent = await this.getDirectParent(path);
    return await parent.getFileHandle(path.getBase(), { create: false });
  }

  /**
   * @param {FileSystemPath} path
   * @returns {Promise<ReadableStream<Uint8Array>>}
   */
  async getFileStream(path) {
    const fileHandle = await this.getFileHandle(path);
    return (await fileHandle.getFile()).stream();
  }

  /** @type {IFileSystemManager['getUnit']} */
  async getUnit(name) {
    if (name !== 'internal') {
      const error = new Error(`Unit "${name}" not found.`);
      error.name = ErrorName.NotFoundError;
      throw error;
    }
    return await new FileSystemUnit(this, this.storage);
  }

  /** @type {IFileSystemManager['hasDirectory']} */
  async hasDirectory(path) {
    try {
      const parentDirectory = await this.getDirectParent(path);
      if (
        await parentDirectory.isSameEntry(await this.storage.getDirectory())
      ) {
        return true;
      }
      await parentDirectory.getDirectoryHandle(path.getBase(), {
        create: false
      });
      return true;
    } catch {
      return false;
    }
  }

  /** @type {IFileSystemManager['hasEntry']} */
  async hasEntry(path) {
    try {
      return (await this.hasFile(path)) || (await this.hasDirectory(path));
    } catch (error) {
      return false;
    }
  }

  /** @type {IFileSystemManager['hasFile']} */
  async hasFile(path) {
    try {
      const parent = await this.getDirectParent(path);
      await parent.getFileHandle(path.getBase(), { create: false });
      return true;
    } catch {
      return false;
    }
  }

  /** @type {IFileSystemManager['readFile']} */
  async readFile(path) {
    const fileHandle = await this.getFileHandle(path);
    return await (await fileHandle.getFile()).text();
  }

  /** @type {IFileSystemManager['writeFile']} */
  async writeFile(path, data) {
    await this.writeStream(
      path,
      new ReadableStream({
        start: (controller) => {
          const encoder = new TextEncoder();
          const uint8Array = encoder.encode(data);
          controller.enqueue(uint8Array);
          controller.close();
        }
      })
    );
  }

  /**
   * @param {FileSystemPath} path
   * @param {ReadableStream<Uint8Array>} stream
   * @returns {Promise<FileSystemFileHandle>}
   */
  async writeStream(path, stream) {
    const parent = await this.getDirectParent(path, { create: true });
    const fileHandle = await parent.getFileHandle(path.getBase(), {
      create: true
    });
    const writeable = await fileHandle.createWritable();
    await stream.pipeTo(writeable);
    return fileHandle;
  }
}

/**
 * @param {'Directory' | 'Entry' | 'File' } kind
 * @returns {Error}
 */
function notFoundError(kind) {
  const error = new Error(`${kind} not found.`);
  error.name = ErrorName.NotFoundError;
  return error;
}

/**
 * @param {'Directory' | 'Entry' | 'File' } kind
 * @param {string} pathString
 * @returns {Error}
 */
function pathExistsError(kind, pathString) {
  const error = new Error(`${kind} ${pathString} already exists.`);
  error.name = ErrorName.PathExistsError;
  return error;
}
